import {
  createAsyncThunk,
  createEntityAdapter,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState, ThunkApi } from 'state/store';
import { Status } from 'utils/customTypes';
import { SLICE_STATUS } from 'utils/constants';
import { UserSummary } from 'types/store/normalized';

/* ============================== STATE SETUP =============================== */
interface LdUserSummariesState {
  status: Status;
  cursor?: string;
  hasMore?: boolean;
}

interface GetListParams {
  pageSize?: number;
  userName?: string;
}

interface GetUserSummariesResponse {
  data: UserSummary[];
  totalCount: number;
  hasMore?: boolean;
  cursor: string;
}

const initialState: LdUserSummariesState = {
  status: SLICE_STATUS.IDLE,
  cursor: '',
  hasMore: false,
};

const adapter = createEntityAdapter<UserSummary>();

const selectLdUserSummariesState = (state: RootState) =>
  state.normalized.ldUserSummaries;
/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(
  _thunkParameters: GetListParams,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().normalized.ldUserSummaries;
  return status !== SLICE_STATUS.LOADING;
}

export const getLdUserSummaries = createAsyncThunk<
  GetUserSummariesResponse,
  GetListParams,
  ThunkApi
>(
  'normalized/ldUserSummary/getLdUserSummaries',
  async (
    { pageSize, userName },
    { getState, extra: { getUserSummariesApi } }
  ) => {
    const { hasMore, cursor } = selectLdUserSummariesState(getState());

    const baseParams = {
      pageSize,
      userName,
      userType: 'ld' as const,
    };

    const queryParams =
      hasMore && cursor ? { ...baseParams, after: cursor } : baseParams;

    const { data, meta } = await getUserSummariesApi().getByIdsPost({
      ...queryParams,
    });

    return {
      data,
      totalCount: meta.totalCount,
      cursor: meta.cursor,
      hasMore: meta.hasMore,
    };
  },
  { condition: concurrencyCondition }
);

/* ================================= REDUCER ================================ */
const ldUserSummariesSlice = createSlice({
  name: 'normalized/ldUserSummary',
  initialState: adapter.getInitialState(initialState),
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getLdUserSummaries.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getLdUserSummaries.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getLdUserSummaries.fulfilled, (state, action) => {
        adapter.upsertMany(state, action.payload.data);
        state.cursor = action.payload.cursor;
        state.hasMore = action.payload.hasMore;
        state.status = SLICE_STATUS.SUCCESS;
      });
  },
});

/* =============================== SELECTORS ================================ */
const defaultSelectors = adapter.getSelectors<RootState>(
  (state) => state.normalized.ldUserSummaries
);

const selectLDHasMore = (state: RootState) =>
  state.normalized.ldUserSummaries.hasMore;

const selectLDStatus = (state: RootState) =>
  state.normalized.ldUserSummaries.status;

const selectLDUserSummaryById = (state: RootState) => (id: string) =>
  defaultSelectors.selectById(state, id);

const selectLDUserSummariesByIds = (state: RootState) => (ids: string[]) =>
  ids
    .map((id) => defaultSelectors.selectById(state, id))
    .filter((summary): summary is UserSummary => summary !== undefined);

export const ldUserSummarySelectors = {
  ...defaultSelectors,
  selectLDStatus,
  selectLDUserSummaryById,
  selectLDUserSummariesByIds,
  selectLDHasMore,
};

export default ldUserSummariesSlice.reducer;
